import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { navCategories } from '@/mocks/navigation';
import { BreadcrumbListSchema, WebPageSchema, getPageSEO } from '@/components/base/SEO';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = navCategories.find((c) => c.slug === slug);
  const [searchQuery, setSearchQuery] = useState('');

  const pageTitle = category
    ? `${category.name} - 网址导航分类 | 51网址导航`
    : '分类未找到 | 51网址导航';
  const pageDesc = category
    ? `${category.name}网址导航分类，收录${category.count}个精选网站。${category.description}。51网址导航提供全面的${category.name}网站入口。`
    : '网址导航分类页面';
  const canonicalUrl = `http://chiguadao.cn/category/${slug}`;

  useEffect(() => {
    document.title = pageTitle;
    document.querySelector('meta[name="description"]')?.setAttribute('content', pageDesc);
    document.querySelector('link[rel="canonical"]')?.setAttribute('href', canonicalUrl);

    const existingScript = document.getElementById('schema-jsonld');
    if (existingScript) existingScript.remove();

    const schemas = [
      WebPageSchema(pageTitle, canonicalUrl, pageDesc),
      BreadcrumbListSchema([
        { name: '首页', url: 'http://chiguadao.cn/' },
        { name: category?.name || '分类', url: canonicalUrl },
      ]),
    ];

    const jsonLd = JSON.stringify({ '@context': 'https://schema.org', '@graph': schemas }, null, 2);
    const script = document.createElement('script');
    script.id = 'schema-jsonld';
    script.type = 'application/ld+json';
    script.textContent = jsonLd;
    document.head.appendChild(script);
  }, [slug, pageTitle, pageDesc, canonicalUrl, category]);

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-heading font-bold text-2xl text-foreground-900 mb-3">分类未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，您查找的分类不存在。</p>
            <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const filteredSites = searchQuery
    ? category.sites.filter(
        (s) =>
          s.name.includes(searchQuery) || s.description.includes(searchQuery)
      )
    : category.sites;

  return (
    <>
      <Navbar />
      <main className="pt-20 min-h-screen">
        <div className="bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { name: '首页', path: '/' },
                { name: '分类导航', path: '/category/yingshi' },
                { name: category.name },
              ]}
            />

            <div className="py-10 md:py-14">
              <div className="flex items-start gap-5 mb-8">
                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${category.color} flex items-center justify-center flex-shrink-0`}>
                  <i className={`${category.icon} text-2xl text-background-50`}></i>
                </div>
                <div>
                  <h1 className="font-heading font-bold text-3xl md:text-4xl text-foreground-900">
                    {category.name}网址导航
                  </h1>
                  <p className="mt-2 text-foreground-500 text-base">
                    {category.description} · 收录
                    <strong className="text-primary-500 mx-1">{category.count}</strong>
                    个精选网站
                  </p>
                </div>
              </div>

              <div className="relative mb-8">
                <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={`在${category.name}分类中搜索网站...`}
                  className="w-full pl-10 pr-4 py-3 bg-background-50 border border-background-200 rounded-xl text-sm text-foreground-800 placeholder-foreground-400 outline-none focus:border-primary-300 transition-colors"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-10">
          {filteredSites.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-foreground-500">没有找到匹配的网站</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredSites.map((site, idx) => (
                <a
                  key={idx}
                  href={site.url}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="flex items-center gap-4 bg-background-50 border border-background-200/70 rounded-xl p-4 hover:border-primary-300 hover:bg-background-50 transition-all group cursor-pointer"
                >
                  <div className="w-10 h-10 rounded-lg bg-background-100 flex items-center justify-center flex-shrink-0 group-hover:bg-primary-50 transition-colors">
                    <i className="ri-global-line text-lg text-foreground-600"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-label font-semibold text-sm text-foreground-900 truncate">{site.name}</h3>
                    <p className="text-xs text-foreground-500 truncate mt-0.5">{site.description}</p>
                  </div>
                  <i className="ri-arrow-right-up-line text-foreground-300 group-hover:text-primary-500 transition-colors"></i>
                </a>
              ))}
            </div>
          )}
        </div>

        <div className="bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-14">
            <h2 className="font-heading font-bold text-xl text-foreground-900 mb-6">其他分类</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {navCategories
                .filter((c) => c.slug !== slug)
                .map((c) => (
                  <Link
                    key={c.id}
                    to={`/category/${c.slug}`}
                    className="flex items-center gap-3 bg-background-100 border border-background-200/70 rounded-xl p-3.5 hover:border-primary-300 transition-all cursor-pointer"
                  >
                    <div className="w-9 h-9 rounded-lg bg-background-200 flex items-center justify-center flex-shrink-0">
                      <i className={`${c.icon} text-sm text-foreground-600`}></i>
                    </div>
                    <span className="text-sm text-foreground-700 font-medium whitespace-nowrap">{c.name}</span>
                  </Link>
                ))}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}